import { store } from '@/redux-store';
import type { RootState } from '@/redux-store/types';
import type {
    EntityKey,
    ObjectFilter,
    RoleConfig,
    TableConfig,
} from '@/types/common/table-config.type';
import type { VerticalMenuDataType } from '@/types/menu.type';
import type { KeyWithFilter } from '../filter/filter';
import { t } from '../lang';
import type { RouteManagerInterface } from '../route/routeManager';
import appRoleConfig from './tableConfigs/app-role';
import assigneeConfig from './tableConfigs/assignee';
import businessTripFormConfig from './tableConfigs/business-trip-form';
import componentCategoryConfig from './tableConfigs/component-category';
import componentDetailConfig from './tableConfigs/component-detail';
import componentGroupConfig from './tableConfigs/component-group';
import componentRecommendationConfig from './tableConfigs/component-recommendation';
import componentSKUConfig from './tableConfigs/component-sku';
import componentTypeConfig from './tableConfigs/component-type';
import customerConfig from './tableConfigs/customer';
import deviceConfig from './tableConfigs/device';
import deviceGroupConfig from './tableConfigs/device-group';
import deviceModelConfig from './tableConfigs/device-model';
import deviceSKUConfig from './tableConfigs/device-sku';
import deviceTypeConfig from './tableConfigs/device-type';
import dynamicTaskConfig from './tableConfigs/dynamic-task';
import dynamicTicketConfig from './tableConfigs/dynamic-ticket';
import errorDetailConfig from './tableConfigs/error-detail';
import errorDetailTaskCheckConfig from './tableConfigs/error-detail-task-check';
import errorTypeConfig from './tableConfigs/error-type';
import filterReplacementScheduleConfig from './tableConfigs/filter-replacement-schedule';
import maintenanceHistoryConfig from './tableConfigs/maintenance-history';
import operationPerformanceConfig from './tableConfigs/operation-performance';
import qualityManagementConfig from './tableConfigs/quality-management';
import reminderScheduleConfig from './tableConfigs/reminder-schedule';
import taskCheckConfig from './tableConfigs/task-check';
import taskCheckTypeConfig from './tableConfigs/task-check-type';
import templateCheckConfig from './tableConfigs/template-check';
import ticketConfig from './tableConfigs/ticket';
import ticketTypeConfig from './tableConfigs/ticket-type';
import usageTypeConfig from './tableConfigs/usage-type';
import violationDetailConfig from './tableConfigs/violation-detail';
import violationDetailTaskCheckConfig from './tableConfigs/violation-detail-task-check';
import violationTypeConfig from './tableConfigs/violation-type';

export abstract class Role {
    routeManager: RouteManagerInterface;

    protected configMap: Record<EntityKey, TableConfig> = {
        task: {
            columns: taskCheckConfig.columns,
            filters: taskCheckConfig.filters,
            queryFilter: taskCheckConfig.queryFilter,
        },
        ticket: {
            columns: ticketConfig.columns,
            filters: ticketConfig.filters,
            queryFilter: ticketConfig.queryFilter,
        },
        businessTripForm: {
            columns: businessTripFormConfig.columns,
            filters: businessTripFormConfig.filters,
            queryFilter: businessTripFormConfig.queryFilter,
        },
        device: {
            columns: deviceConfig.columns,
            filters: deviceConfig.filters,
            queryFilter: deviceConfig.queryFilter,
        },
        errorDetailTaskCheck: {
            columns: errorDetailTaskCheckConfig.columns,
            filters: errorDetailTaskCheckConfig.filters,
            queryFilter: errorDetailTaskCheckConfig.queryFilter,
        },
        violationDetailTaskCheck: {
            columns: violationDetailTaskCheckConfig.columns,
            filters: violationDetailTaskCheckConfig.filters,
            queryFilter: violationDetailTaskCheckConfig.queryFilter,
        },
        qualityManagement: {
            columns: qualityManagementConfig.columns,
            filters: qualityManagementConfig.filters,
            queryFilter: qualityManagementConfig.queryFilter,
        },
        componentRecommendation: {
            columns: componentRecommendationConfig.columns,
            filters: componentRecommendationConfig.filters,
            queryFilter: componentRecommendationConfig.queryFilter,
        },
        assignee: {
            columns: assigneeConfig.columns,
            filters: assigneeConfig.filters,
            queryFilter: assigneeConfig.queryFilter,
        },
        customer: {
            columns: customerConfig.columns,
            filters: customerConfig.filters,
            queryFilter: customerConfig.queryFilter,
        },
        appRole: {
            columns: appRoleConfig.columns,
            filters: appRoleConfig.filters,
            queryFilter: appRoleConfig.queryFilter,
        },
        violationDetail: {
            columns: violationDetailConfig.columns,
            filters: violationDetailConfig.filters,
            queryFilter: violationDetailConfig.queryFilter,
        },
        violationType: {
            columns: violationTypeConfig.columns,
            filters: violationTypeConfig.filters,
            queryFilter: violationTypeConfig.queryFilter,
        },
        errorDetail: {
            columns: errorDetailConfig.columns,
            filters: errorDetailConfig.filters,
            queryFilter: errorDetailConfig.queryFilter,
        },
        errorType: {
            columns: errorTypeConfig.columns,
            filters: errorTypeConfig.filters,
            queryFilter: errorTypeConfig.queryFilter,
        },
        usageType: {
            columns: usageTypeConfig.columns,
            filters: usageTypeConfig.filters,
            queryFilter: usageTypeConfig.queryFilter,
        },
        templateCheck: {
            columns: templateCheckConfig.columns,
            filters: templateCheckConfig.filters,
            queryFilter: templateCheckConfig.queryFilter,
        },
        taskCheckType: {
            columns: taskCheckTypeConfig.columns,
            filters: taskCheckTypeConfig.filters,
            queryFilter: taskCheckTypeConfig.queryFilter,
        },
        ticketType: {
            columns: ticketTypeConfig.columns,
            filters: ticketTypeConfig.filters,
            queryFilter: ticketTypeConfig.queryFilter,
        },
        componentCategory: {
            columns: componentCategoryConfig.columns,
            filters: componentCategoryConfig.filters,
            queryFilter: componentCategoryConfig.queryFilter,
        },
        componentSKU: {
            columns: componentSKUConfig.columns,
            filters: componentSKUConfig.filters,
            queryFilter: componentSKUConfig.queryFilter,
        },
        componentGroup: {
            columns: componentGroupConfig.columns,
            filters: componentGroupConfig.filters,
            queryFilter: componentGroupConfig.queryFilter,
        },
        componentType: {
            columns: componentTypeConfig.columns,
            filters: componentTypeConfig.filters,
            queryFilter: componentTypeConfig.queryFilter,
        },
        deviceGroup: {
            columns: deviceGroupConfig.columns,
            filters: deviceGroupConfig.filters,
            queryFilter: deviceGroupConfig.queryFilter,
        },
        deviceModel: {
            columns: deviceModelConfig.columns,
            filters: deviceModelConfig.filters,
            queryFilter: deviceModelConfig.queryFilter,
        },
        deviceSKU: {
            columns: deviceSKUConfig.columns,
            filters: deviceSKUConfig.filters,
            queryFilter: deviceSKUConfig.queryFilter,
        },
        deviceType: {
            columns: deviceTypeConfig.columns,
            filters: deviceTypeConfig.filters,
            queryFilter: deviceTypeConfig.queryFilter,
        },
        componentDetail: {
            columns: componentDetailConfig.columns,
            filters: componentDetailConfig.filters,
            queryFilter: componentDetailConfig.queryFilter,
        },
        operationPerformance: {
            columns: operationPerformanceConfig.columns,
            filters: operationPerformanceConfig.filters,
            queryFilter: operationPerformanceConfig.queryFilter,
        },
        filterReplacementSchedule: {
            columns: filterReplacementScheduleConfig.columns,
            filters: filterReplacementScheduleConfig.filters,
            queryFilter: filterReplacementScheduleConfig.queryFilter,
        },
        maintenanceHistory: {
            columns: maintenanceHistoryConfig.columns,
            filters: maintenanceHistoryConfig.filters,
            queryFilter: maintenanceHistoryConfig.queryFilter,
        },
        reminderSchedule: {
            columns: reminderScheduleConfig.columns,
            filters: reminderScheduleConfig.filters,
            queryFilter: reminderScheduleConfig.queryFilter,
        },
        dynamicTicket: {
            columns: dynamicTicketConfig.columns,
            filters: dynamicTicketConfig.filters,
            queryFilter: dynamicTicketConfig.queryFilter,
        },
        dynamicTask: {
            columns: dynamicTaskConfig.columns,
            filters: dynamicTaskConfig.filters,
            queryFilter: dynamicTaskConfig.queryFilter,
        },
    };

    public constructor(routeManager: RouteManagerInterface) {
        this.routeManager = routeManager;
    }

    public getSidebar(): VerticalMenuDataType[] {
        const routeMap = this.routeManager.getRouteMap();

        return [
            routeMap['home'],
            {
                id: 'cong-viec',
                label: t('Công việc'),
                isSection: true,
                permissions: [
                    'Ticket.*',
                    'TicketTemplate.*',
                    'TaskCheck.*',
                    'TaskTemplateDevice.*',
                    'ErrorDetailTaskCheck.*',
                    'ViolationDetailTaskCheck.*',
                    'BusinessTripForm.*',
                    'Coordination.*',
                    'QualityManagement.*',
                ],
                children: [
                    routeMap[this.routeManager.COORDINATION],
                    {
                        id: 'Ticket',
                        label: t('Ticket'),
                        icon: 'ticket',
                        permissions: ['Ticket.*', 'TicketTemplate.*'],
                        children: [
                            routeMap[this.routeManager.TICKET],
                            routeMap[this.routeManager.DYNAMIC_TICKET],
                        ],
                    },
                    {
                        id: 'TaskCheck',
                        label: t('Task'),
                        icon: 'task',
                        permissions: ['TaskCheck.*', 'TaskTemplateDevice.*'],
                        children: [
                            routeMap[this.routeManager.TASK],
                            routeMap[this.routeManager.DYNAMIC_TASK],
                        ],
                    },

                    routeMap[this.routeManager.ERROR_DETAIL_TASK_CHECK],
                    routeMap[this.routeManager.VIOLATION_CUSTOMER],
                    routeMap[this.routeManager.QUALITY_MANAGEMENT],
                ],
            },
            {
                id: 'danh-muc-thiet-bi',
                label: t('Danh mục thiết bị'),
                isSection: true,
                permissions: ['Device.*', 'PartSuggestion.*'],
                children: [
                    routeMap[this.routeManager.DEVICE],
                    routeMap[this.routeManager.COMPONENT_RECOMMENDATION],
                ],
            },
            {
                id: 'nguoi-dung',
                label: t('Người dùng', 'Navbar'),
                isSection: true,
                permissions: ['Customer_Get_List', 'Admin_Get_User_List'],
                children: [
                    routeMap[this.routeManager.STAFF],
                    routeMap[this.routeManager.CUSTOMER],
                ],
            },
            {
                id: 'bao-cao-thong-ke',
                label: t('Biểu mẫu', 'Navbar'),
                isSection: true,
                permissions: [
                    // 'Dashboard_Get_Task_Repeat',
                    // 'Dashboard_Get_Multidimensional_Report',
                    // 'Dashboard_Get_Task_Processing',
                    // 'Dashboard_Get_Part_Suggestion_Replace',
                    // 'Dashboard_Get_Operation_Performance',
                    'Reports_Performance',
                    'Reports_Production_Comparison',
                    'Reports_Device_Maintenance_Schedule',
                    'Reports_Spare_Part_Replacement_Rate',
                ],
                children: [
                    {
                        id: 'bao-cao',
                        label: t('Báo cáo'),
                        icon: 'report',
                        permissions: [
                            // 'Dashboard_Get_Task_Repeat',
                            // 'Dashboard_Get_Multidimensional_Report',
                            // 'Dashboard_Get_Task_Processing',
                            // 'Dashboard_Get_Part_Suggestion_Replace',
                            // 'Dashboard_Get_Operation_Performance',
                            'Reports_Performance',
                            'Reports_Production_Comparison',
                            'Reports_Device_Maintenance_Schedule',
                            'Reports_Spare_Part_Replacement_Rate',
                        ],
                        children: [
                            routeMap[
                                this.routeManager
                                    .REPORT_PRODUCTIVITY_AND_MACHINE_CAPACITY
                            ],
                            routeMap[
                                this.routeManager.REPORT_COMPARE_PRODUCTIVITY
                            ],
                            routeMap[
                                this.routeManager.REPORT_MAINTENANCE_SCHEDULE
                            ],
                            routeMap[
                                this.routeManager.REPORT_REPLACEMENT_RATIO
                            ],

                            // routeMap[
                            //     this.routeManager.DASHBOARD_PERIODIC_OPERATION
                            // ],
                            // routeMap[this.routeManager.DASHBOARD_TASK_REPEAT],
                            // routeMap[
                            //     this.routeManager
                            //         .DASHBOARD_TASK_OF_EACH_TECHNICIAN
                            // ],
                            // routeMap[
                            //     this.routeManager
                            //         .DASHBOARD_COMPONENT_RECOMMENDATION_AND_REPLACEMENT
                            // ],
                            // routeMap[
                            //     this.routeManager
                            //         .DASHBOARD_MULTIDIMENSIONAL_SUMMARY
                            // ],
                        ],
                    },
                ],
            },
            {
                id: 'cau-hinh',
                label: t('Cấu hình'),
                isSection: true,
                permissions: [
                    'TemplateCheck.*',
                    'TaskCheckType.*',
                    'TicketType.*',
                    'UsageType.*',
                    'DeviceGroup.*',
                    'DeviceType.*',
                    'DeviceSKU.*',
                    'DeviceModel.*',
                    'PartGroup.*',
                    'PartType.*',
                    'PartSKU.*',
                    'PartCategory.*',
                    'Translation.*',
                ],
                children: [
                    {
                        id: 'cau-hinh-thiet-bi',
                        label: t('Cấu hình thiết bị'),
                        icon: 'deviceConfig',
                        permissions: [
                            'DeviceGroup.*',
                            'DeviceType.*',
                            'DeviceSKU.*',
                            'DeviceModel.*',
                        ],
                        children: [
                            routeMap[this.routeManager.DEVICE_TYPE],
                            routeMap[this.routeManager.DEVICE_GROUP],
                            routeMap[this.routeManager.DEVICE_SKU],
                            routeMap[this.routeManager.DEVICE_MODEL],
                        ],
                    },
                    {
                        id: 'cau-hinh-thanh-phan',
                        label: t('Cấu hình thành phần'),
                        icon: 'componentConfig',
                        permissions: [
                            'PartGroup.*',
                            'PartType.*',
                            'PartSKU.*',
                            'PartCategory.*',
                        ],
                        children: [
                            routeMap[this.routeManager.COMPONENT_CATEGORY],
                            routeMap[this.routeManager.COMPONENT_TYPE],
                            routeMap[this.routeManager.COMPONENT_GROUP],
                            routeMap[this.routeManager.COMPONENT_SKU],
                        ],
                    },
                    {
                        id: 'cau-hinh-tac-vu',
                        label: t('Tác vụ'),
                        icon: 'jobConfig',
                        permissions: [
                            'TemplateCheck.*',
                            'TaskCheckType.*',
                            'TicketType.*',
                            'UsageType.*',
                            'ErrorType.*',
                            'Error.*',
                            'ViolationType.*',
                            'Violation.*',
                        ],
                        children: [
                            routeMap[this.routeManager.TICKET_TYPE],
                            routeMap[this.routeManager.TASK_TYPE],
                            routeMap[this.routeManager.TEMPLATE_CHECK],
                            routeMap[this.routeManager.USAGE_TYPE],
                            routeMap[this.routeManager.ERROR_TYPE],
                            routeMap[this.routeManager.ERROR],
                            routeMap[this.routeManager.VIOLATION_TYPE],
                            routeMap[this.routeManager.VIOLATION],
                        ],
                    },
                    routeMap[this.routeManager.MULTI_LANGUAGE_CONFIG],
                ],
            },
            {
                id: 'phan-quyen',
                label: t('Phân quyền'),
                isSection: true,
                permissions: [
                    'Admin_Assign_Role.*',
                    'Admin_Get_Permission_Role',
                ],
                children: [routeMap[this.routeManager.ROLE]],
            },
        ];
    }

    public getInformationHomeRoute(): {
        routeKey: string;
        params: Record<string, any>;
    } {
        return {
            routeKey: this.routeManager.HOME,
            params: {},
        };
    }

    public canLeaveReview(): boolean {
        return false;
    }

    public canEditReview(userId: string): boolean {
        const state = store.getState() as RootState;

        const currentUserId = state.auth.user?.id;

        return currentUserId === userId;
    }

    public canUpdateSignature(): boolean {
        return false;
    }

    public getFilter<K extends EntityKey>(key: K, ...args: any[]) {
        const originalFilters = this.configMap[key].filters(...args);

        const state = store.getState() as RootState;

        const userRole = state.auth.userInformation?.clientRole;

        const filters = originalFilters.map(filter => {
            const data: KeyWithFilter = {
                ...filter,
                key: [],
            };

            filter.key.forEach(item => {
                if (typeof item != 'string') {
                    if (this.shouldShowData(item, userRole!)) {
                        data.key.push(item);
                    }
                } else {
                    data.key.push(item);
                }
            });

            return data;
        });

        return filters;
    }

    public getQuery<K extends EntityKey>(key: K, args: ObjectFilter) {
        if (this.configMap[key]?.queryFilter) {
            const originalQuery = this.configMap[key].queryFilter(args);

            const state = store.getState() as RootState;

            const userRole = state.auth.userInformation?.clientRole;

            const query = Object.keys(originalQuery).reduce((acc: any, key) => {
                if (this.shouldShowData(originalQuery[key] ?? {}, userRole!)) {
                    acc[key] = originalQuery[key];
                }

                return acc;
            }, {});

            return query;
        }

        return {};
    }

    public getColumns<K extends EntityKey>(key: K, ...args: any[]) {
        const originalColumns = this.configMap[key].columns(...args);

        const state = store.getState() as RootState;

        const userRole = state.auth.userInformation?.clientRole;

        const columns = originalColumns.filter(column => {
            if (this.shouldShowData(column, userRole!)) {
                return true;
            }

            return false;
        });

        return columns;
    }

    public shouldShowData(row: RoleConfig, userRole: string): boolean {
        if (!row.includeRoles && !row.excludeRoles) {
            return true;
        }

        // high priority for exclude roles
        if (row.excludeRoles) {
            if (row.excludeRoles.includes(userRole)) {
                return false;
            }
        }

        if (row.includeRoles) {
            if (row.includeRoles.includes(userRole)) {
                return true;
            }

            return false;
        }

        return true;
    }

    public abstract defaultRoute(): string;

    public abstract queryById(): null | string;
}
